import { compact } from 'lodash';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Filters_DEPRECATED } from '~/components/Shared/Filters_DEPRECATED/Filters_DEPRECATED';
import * as Filters from '~/components/Shared/Filters_DEPRECATED/types';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { Field } from '~/evifields/src/types/Field';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { Filters as ClauseLibraryFilters } from '~/types/ClauseLibrary';
import { Nullable } from '~/types/Nullable';
import { debounce } from '~/utils/debounce';

const CLAUSE_FILTER_TYPE_ID = 'CLAUSE_FILTER';
const POSITION_FILTER_TYPE_ID = 'POSITION_FILTER';

const ClauseFilter = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [internalQuery, setInternalQuery] = useState<Nullable<string>>(null);
  const filters = useSelector(slices.clauseLibrary.selectors.selectFilters);

  const AsyncClauseOptions: Filters.FieldRendererProps = ({
    Component,
    innerProps,
  }) => {
    const [getFieldValues, result] =
      api.endpoints.clauseLibrarySearchClauses.useLazyQuery();
    const debouncedGetClauses = useCallback(debounce(getFieldValues, 300), [
      getFieldValues,
    ]);
    const { data, error, isFetching } = result;
    const options = (data?.data || []).map((node) => ({
      value: node.id,
      label: node.attributes.name,
    }));
    const asyncOptions = { data: options, error, isFetching };
    const fetchOptions = (name = '') => {
      debouncedGetClauses({ name }, true);
    };

    const asyncProps = {
      asyncOptions,
      onMount: fetchOptions,
      onQuery: fetchOptions,
    };

    return <Component asyncProps={asyncProps} innerProps={innerProps} />;
  };

  const FIELDS = {
    [CLAUSE_FILTER_TYPE_ID]: {
      id: CLAUSE_FILTER_TYPE_ID,
      label: intl.formatMessage({
        id: 'ClauseFilter.clauseLabel',
        defaultMessage: 'Clause',
        description: 'Label for clause filter field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      overrideOperatorLabels: {
        contains_any: intl.formatMessage({
          id: 'ClauseFilter.containsOperator',
          defaultMessage: 'contains',
          description: 'Contains operator label',
        }),
      },
      settings: {
        __testEnableSelectAll: () => false,
        options: [],
      },
      render: AsyncClauseOptions,
      serialize: () =>
        intl.formatMessage({
          id: 'ClauseFilter.oneValueSerialized',
          defaultMessage: '1 value',
          description: 'Serialized value display for single selection',
        }),
    },
    [POSITION_FILTER_TYPE_ID]: {
      id: POSITION_FILTER_TYPE_ID,
      label: intl.formatMessage({
        id: 'ClauseFilter.positionLabel',
        defaultMessage: 'Position',
        description: 'Label for position filter field',
      }),
      type: 'enum',
      whitelistOperatorIds: ['equals'],
      overrideOperatorLabels: {
        equals: intl.formatMessage({
          id: 'ClauseFilter.equalsOperator',
          defaultMessage: 'equals',
          description: 'Equals operator label',
        }),
      },
      settings: {
        __testEnableSelectAll: () => false,
        options: [
          {
            label: intl.formatMessage({
              id: 'ClauseFilter.preferredOption',
              defaultMessage: 'Preferred',
              description: 'Preferred position option',
            }),
            value: 'Preferred',
          },
          {
            label: intl.formatMessage({
              id: 'ClauseFilter.fallbackOption',
              defaultMessage: 'Fallback',
              description: 'Fallback position option',
            }),
            value: 'Fallback',
          },
          {
            label: intl.formatMessage({
              id: 'ClauseFilter.walkAwayOption',
              defaultMessage: 'Walk away',
              description: 'Walk away position option',
            }),
            value: 'Walk away',
          },
        ],
      },
    },
  };

  const DEFAULT_FILTERS = compact([
    {
      id: CLAUSE_FILTER_TYPE_ID,
      fieldId: CLAUSE_FILTER_TYPE_ID,
      operatorId: 'contains_any',
      values: [],
    },
    {
      id: POSITION_FILTER_TYPE_ID,
      fieldId: POSITION_FILTER_TYPE_ID,
      operatorId: 'equals',
      values: [],
    },
  ]);

  const onSearchTextChange = useDebounce(
    (
      query: Nullable<string>,
      initialFilters: Nullable<ClauseLibraryFilters>,
    ) => {
      dispatch(
        slices.clauseLibrary.actions.setFilter({
          ...initialFilters,
          query,
        }),
      );
    },
    INPUT_DEBOUNCE_MS,
    [filters?.query],
  );

  const onFilterChange = (chips: Filter[]) => {
    dispatch(
      slices.clauseLibrary.actions.setFilter({
        ...filters,
        chips,
      }),
    );
  };

  return (
    <Layout direction="row" spacing={1}>
      <Layout justify="end" align="center" flex={1}>
        <TextInput
          type="search"
          name="clause-lib-search-language"
          placeholder={intl.formatMessage({
            id: 'ClauseFilter.searchPlaceholder',
            defaultMessage: 'Search Clauses and Language',
            description: 'Placeholder text for clause search input',
          })}
          value={internalQuery ?? filters?.query ?? ''}
          onChange={(q) => {
            setInternalQuery(q);
            onSearchTextChange(q, filters);
          }}
        />
      </Layout>
      <Layout justify="end" align="center">
        <Filters_DEPRECATED
          fields={FIELDS as Record<FieldId, Field>}
          filters={(filters?.chips ?? DEFAULT_FILTERS) as Filter[]}
          groups={[]}
          onChange={onFilterChange}
          options={{ disableManageFilters: true, disableRemoveFilter: true }}
        />
      </Layout>
    </Layout>
  );
};

export default ClauseFilter;
